import Link from 'next/link'
import { useMemo } from 'react'

import { Text } from '@wartek-id/text-v1'
import { Spinner } from '@wartek-id/spinner'
import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'

import CircularProgress, {
  CircularLock,
} from 'app/KinerjaPage/components/CircularProgress'
import { Header } from 'app/KinerjaShared/components/Header'
import { PENGELOLAAN_KINERJA_KEPALA_SKB_TIM_KINERJA_PATH } from 'app/KinerjaShared/constants/path'
import useRoleContext from 'app/KinerjaShared/hooks/useRoleContext'
import { getUserDisplayName } from 'utils/user'
import { useSafeBackNavigation } from 'app/KinerjaShared/hooks/useSafeBackNavigation'
import usePenilaianCountPs from 'app/KinerjaPSPage/hooks/usePenilaianCountPs'
import { PANDUAN_GENERAL_URL } from 'app/KinerjaShared/utils/panduan'

export function PenilaianPenetapanHub() {
  // -----------------------------------------
  // Resolve greeting message
  // -----------------------------------------
  const { profilePs, isLoading: isProfileLoading } = useRoleContext()
  const greetingMessage = useMemo(() => {
    if (isProfileLoading || !profilePs?.nama) {
      return 'Selamat datang!'
    }

    return `Selamat datang, ${getUserDisplayName(profilePs.nama)}!`
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profilePs])

  // Penilaian
  const { isLoading: isLoadingPenilaianCount, data: counterPenilaian } =
    usePenilaianCountPs({
      enabled: true,
    })

  const penilaian = useMemo(() => {
    if (!counterPenilaian)
      return {
        count: 0,
        total: 0,
      }

    return {
      count: counterPenilaian.finish,
      isCompleted:
        counterPenilaian.all > 0 &&
        counterPenilaian.finish === counterPenilaian.all,
      total: counterPenilaian.all,
    }
  }, [counterPenilaian])

  // -----------------------------------------
  // Handle back navigation with fallback to home page
  // -----------------------------------------
  const { handleBack } = useSafeBackNavigation({
    fallbackRoute: PENGELOLAAN_KINERJA_KEPALA_SKB_TIM_KINERJA_PATH,
  })
  // -----------------------------------------

  return (
    <main className="min-h-full px-8 pt-12 pb-8 bg-gray-10">
      <div className="mx-auto max-w-7xl">
        <Header
          title="Pemantauan Kinerja dan Rekomendasi Predikat Kinerja"
          className="mb-9 print:hidden"
          rightNode={
            <section className="py-1 bg-white rounded">
              <Link href={PANDUAN_GENERAL_URL} passHref legacyBehavior>
                <Button
                  as="a"
                  variant="ghost"
                  target="_blank"
                  rel="noreferrer"
                  className="flex gap-1.5"
                  size="md"
                  type={undefined}
                >
                  <Icon fontSize="sm" color="informational">
                    help
                  </Icon>
                  Ke Pusat Informasi
                </Button>
              </Link>
            </section>
          }
          onBack={handleBack}
        />

        <section className="mb-9">
          <header className="p-4 space-y-1 text-white rounded-t-lg bg-gradient-to-r from-[#C41B63] to-[#FBC6E0]">
            <Text
              className="overflow-wrap-anywhere line-clamp-1"
              color="inherit"
              variant="heading-lg"
              as="h2"
            >
              {greetingMessage}
            </Text>
            <Text color="inherit">
              Pemantauan Kinerja yang Anda lakukan merupakan aspek penting dalam
              peningkatan kinerja wilayah binaan Anda.
            </Text>
          </header>

          <div className="px-8 py-6 bg-white rounded-b-lg">
            <div className="space-y-6">
              <div className="flex items-center py-6 pl-6 pr-8 space-x-6 border rounded border-subdued">
                <div className="flex items-center justify-center flex-shrink-0 w-20 h-20">
                  {isLoadingPenilaianCount ? (
                    <Spinner size="lg" />
                  ) : (
                    <CircularProgress
                      value={penilaian.count}
                      maxValue={penilaian.total}
                    />
                  )}
                </div>
                <div className="w-full space-y-2">
                  <Text variant="heading-md" as="h3">
                    Pemantauan Kinerja
                  </Text>
                  <Text color="subdued">
                    Cek Praktik Kinerja, Perilaku Kerja, dan lainnya.
                  </Text>
                </div>
                <Link
                  href={`${PENGELOLAAN_KINERJA_KEPALA_SKB_TIM_KINERJA_PATH}/pelaksanaan/pemantauan`}
                  passHref
                >
                  <Button
                    as="a"
                    color={penilaian.isCompleted ? 'white' : 'black'}
                    className="flex-shrink-0 w-40"
                    type={undefined}
                  >
                    {penilaian.isCompleted ? 'Cek' : 'Lakukan'}
                    <span className="sr-only">Pemantauan Kinerja</span>
                  </Button>
                </Link>
              </div>

              <div className="flex items-center py-6 pl-6 pr-8 space-x-6 border rounded border-subdued">
                <div className="flex items-center justify-center flex-shrink-0 w-20 h-20">
                  <CircularLock />
                </div>
                <div className="w-full space-y-2">
                  <Text variant="heading-md" as="h3">
                    Rekomendasi Predikat Kinerja
                  </Text>
                  <Text color="subdued">
                    Rekomendasi yang Anda lakukan akan dikirim ke Pejabat
                    Penilai Kinerja.
                  </Text>
                </div>
                <Link
                  // TODO: Change to the correct path when the feature is ready
                  href={`${PENGELOLAAN_KINERJA_KEPALA_SKB_TIM_KINERJA_PATH}/penilaian-kinerja`}
                  passHref
                >
                  <Button
                    as="a"
                    type={undefined}
                    className="flex-shrink-0 w-40"
                    disabled={true}
                  >
                    Lakukan
                    <span className="sr-only">Penetapan Predikat Kinerja</span>
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  )
}
